/**
 * Dependency imports
 */
import React from 'react';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  UserCredential,
  fetchSignInMethodsForEmail 
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';

/**
 * Local imports
 */
import { auth } from '@/firebase/auth';

/**
 * Types
 */
type SignupStatus = 'idle' | 'pending' | 'complete' | 'error';

interface SignupState {
  info: UserCredential | Error | null;
  status: SignupStatus;
  verificationEmailSent: boolean;
}

type SignupHookReturn = [
  signup: (email: string, password: string) => Promise<void>,
  state: SignupState
];

export const useEmailSignup = (): SignupHookReturn => {
  const [state, setState] = React.useState<SignupState>({
    info: null,
    status: 'idle',
    verificationEmailSent: false
  });

  const signup = async (email: string, password: string): Promise<void> => {
    setState(prev => ({ ...prev, status: 'pending' }));

    try {
      // First check if email exists
      const methods = await fetchSignInMethodsForEmail(auth, email);
      
      if (methods.length > 0) {
        // If email exists, throw a properly formatted error
        const error = new Error('This email address is already registered', {
          cause: 'auth/email-already-in-use'
        });
        
        setState({
          status: 'error',
          info: error,
          verificationEmailSent: false
        });
        
        throw error;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send verification email
      await sendEmailVerification(userCredential.user, {
        url: process.env.NEXT_PUBLIC_APP_URL || window.location.origin,
        handleCodeInApp: true,
      });

      setState({
        status: 'complete',
        info: userCredential,
        verificationEmailSent: true
      });
    } catch (err) {
      let error: Error;
      
      if (err instanceof FirebaseError) {
        // Handle Firebase specific errors
        error = new Error(err.message, {
          cause: err.code
        });
      } else if (err instanceof Error) {
        // Pass through our custom errors
        error = err;
      } else {
        // Handle unknown errors
        error = new Error('An unexpected error occurred', {
          cause: 'unknown'
        });
      }

      setState({
        status: 'error',
        info: error,
        verificationEmailSent: false
      });
      
      throw error;
    }
  };

  return [signup, state];
};